/**
 * What a livery actually *is*, as data.
 *
 * The catalogue owns the price, the name and the two swatch colours; this owns
 * how the tank looks. Same split as `weapons.ts` and for the same reason — a
 * paint scheme is code-and-asset shaped, and a Postgres column per texture path
 * would mean a migration every time a livery got more interesting. The join is
 * on the item id, exactly as `HULL_PROFILES` joins on a hull's.
 *
 * Shared rather than frontend-only because **the server has to resolve which
 * livery a player is wearing**: `TankSpec.paintId` is filled in from the
 * player's own account and the `loadout` event carries it to everybody else, so
 * a client cannot assert its own paint any more than it can assert its own
 * hull. The file is browser-safe — no Nest, no Prisma, no Node, no clock, no
 * randomness — for the same reason `weapons.ts` is.
 *
 * ── Why a livery is not a colour ─────────────────────────────────────────────
 *
 * Six PAINT rows have existed since the garage did, carrying `colorPrimary` and
 * `colorSecondary`, and **they reached nothing**. Not `TankSpec`, not the
 * arena, not the hangar, not a thumbnail — the only consumer was a flat SVG
 * plan drawing in the garage sidebar, and the Liveries tab had been taken out
 * of the shop because a hex pair changes nothing on an authored GLB. The shop
 * was selling a swatch.
 *
 * The reason a hex pair could not work is worth writing down, because it is the
 * constraint that shapes everything here. The hulls are image-to-3D meshes and
 * each one carries **one** PBR material over an xatlas UV unwrap — hundreds of
 * tiny islands scattered across a 1024² atlas. A colour multiplied over that
 * flattens the artist's own paint; a *texture* mapped through that UV is
 * scrambled into confetti. Neither is a livery.
 *
 * So the pattern is projected in **object space** instead, triplanar, by a
 * material plugin that leaves the authored albedo, normal and roughness maps
 * exactly where they are and blends over the top. See
 * `frontend/src/game/livery-material.ts`. The upshot for this file is that
 * `pattern` is a tiling texture measured in **metres of hull**, not in UV
 * repeats — it has no relationship to any mesh's unwrap, which is what lets one
 * image serve all 525 hull/turret pairs.
 *
 * ── The belt is not part of a livery, and that is the second design call ─────
 *
 * A paint scheme stops at the paint. The track belts are generated geometry
 * with UVs this codebase owns, so they *could* be repainted per player with no
 * projection trickery at all — and for a while they were, through `tread`,
 * `treadTint` and `treadSeamless` on this interface. They are not any more:
 * every tank in the game runs the stock belt, and `tank.ts` owns its texture
 * and its rubber colour as module constants.
 *
 * Two reasons, in order of weight:
 *
 * **A belt is running gear, not bodywork.** Tracks are the dirtiest and most
 * frequently replaced part of a tank and the first place any paint comes off,
 * so a livery that recoloured them was making a claim about the one part of the
 * vehicle whose look nobody chose. It also spent a colour on the part of a tank
 * that carries no meaning, which is the concern the old `MAX_BELT_COOL_BIAS`
 * bound existed to police and is now answered by there being nothing to police.
 *
 * **Two hulls have no belts at all.** The Drover and the Colossus ride on tyres
 * the artist drew (`HullProfile.wheeled`), so there is no geometry for a tread
 * to land on and none may be generated. While the belt was half the product
 * that made a livery a *partial* purchase on those two — which the shop card
 * had to warn about before the buy, and which is the one outcome a cosmetic
 * must never produce. With the belt out of the product a livery is now exactly
 * the same purchase on all twenty-one hulls, so the caveat is gone from the
 * card rather than merely being worded carefully.
 *
 * What it costs is the belt as a place to say something: the gilt tank rolls on
 * ordinary rubber. The art is still in `/assets/liveries/tread-*.webp` and the
 * mechanism is one field away in the history — where `treadTint`'s own lesson
 * is also recorded, and is worth reading before anybody puts it back: the tint
 * multiplies a *lit* texture, so a swing of 42 on blue moved the rendered belt
 * by six, and a belt whose art carries a cast cannot be tinted out of it.
 *
 * **A fourth `ItemKind` is still not the answer**, and that half of the old
 * reasoning is untouched: `ItemKind` is a Prisma enum with a closed mirror in
 * `catalogue.data.ts`, `stat-resolution.ts`, `garage-provision`, the frontend's
 * `api.ts` and `GarageScreen.vue`; `GarageService.equip` is a hard-coded table
 * annotated with a type that *widens silently*, so a missing row is an
 * unequippable item with no compile error. Selling belts on their own would
 * cost all of that and would put the wheeled hulls straight back in the
 * position above.
 *
 * ── What survives this renderer, and what the shelf learned the hard way ──────
 *
 * The first shelf shipped nine paints and **the three dearest ones were the three
 * that did not work.** That is not a coincidence and it is not taste; it is one
 * property of the pipeline, stated twice below as a bound with a test so the next
 * livery is designed *for* the renderer instead of against it.
 *
 * **1. There is no image-based lighting in this game, so metal has nothing to
 * reflect.** `grep -rn "environmentTexture\|createDefaultEnvironment\|HDRCubeTexture"`
 * over the whole frontend returns nothing: the scene is a `SkyMaterial`, a
 * `DirectionalLight` and a hemispheric fill, by the deliberate decision argued in
 * `environment.ts`. A PBR surface splits incoming light by `metallic` — the
 * diffuse term is scaled by `(1 - metallic)` and the rest is handed to a
 * reflection — so at `metallic: 0.95` **95% of a livery's colour is paid into a
 * reflection of an environment that does not exist**, and a low `roughness`
 * concentrates what little the sun gives back until it clips to white.
 *
 * That is precisely what `Gilded` was: *gold leaf on a war machine*, 6,000
 * credits, `metallic: 0.95, roughness: 0.16`, and a flat bone-cream tank on the
 * battlefield. What gave it away at the time was its belt — then part of a
 * livery, and a `StandardMaterial` that never went through the metallic split at
 * all — being the only gold on the vehicle. `Scorched Iron` failed the same way
 * one notch down at 0.80. Both are fixed here by lowering the finish rather than
 * by adding an environment texture: an IBL would relight every material on every
 * tank and every prop in the game, which is a rendering decision with its own
 * review, not a paint repair. `MAX_LIVERY_METALLIC` is the bound, and
 * `Foundry Hazard` — the best-looking item on the shelf, and the only one whose
 * identity partly survives the team mix — sits just under it at 0.40.
 *
 * **2. The pattern is re-lit by the model's own luminance, so pale patterns with
 * internal contrast survive and dark saturated ones are crushed.** The shader
 * multiplies the pattern by `SHADE_FLOOR + SHADE_GAIN * luma(albedo)` before
 * mixing it in — that is what keeps hatches and weld lines reading *through* the
 * paint, and it is not negotiable per livery. The consequence is a design rule:
 * a scheme that says what it is through **light-against-dark shapes** (desert,
 * winter, urban splinter, hazard diagonals) arrives intact, and a scheme that
 * says what it is through **a saturated hue at low value** (the temper blues on
 * scorched) or through **fine high-frequency detail** (the halftone dots on the
 * withdrawn ambush pattern, which were sub-pixel at 80 m) does not. Author the
 * next pattern as a value structure first and a colour second.
 */

/**
 * The most metal a livery may claim, given that nothing in the scene is
 * reflectable.
 *
 * Not a physical limit — a real gold leaf is 1.0 — but the limit at which this
 * renderer still shows a livery the colour it was authored in. Above it the
 * albedo is progressively paid into a reflection of an empty environment and the
 * paint drains to grey, which is how a 6,000-credit gold scheme shipped as a
 * bone-cream tank.
 *
 * **Measured against the shelf rather than picked.** Every livery that reads
 * correctly in `livery-snapshots/` is at or under 0.40 — `paint_hazard` 0.40,
 * `paint_crimson` 0.35, `paint_urban` 0.25 — and both that failed were at 0.80
 * and 0.95. The bound sits just above the working maximum so a new livery has
 * room to be as metallic as the best one already is and no more.
 *
 * If an environment texture is ever added, this is the constant to raise, and
 * raising it is the *last* step of that change rather than the first.
 */
export const MAX_LIVERY_METALLIC = 0.45;

/**
 * How a tank wearing this livery is painted.
 *
 * Every field is inert at its default, the same discipline `WeaponProfile`
 * follows: `NO_LIVERY` below states nothing and the renderer's plugin stays
 * switched off entirely, so the free paint is not "a livery that happens to
 * look stock" — it is the code path that existed before liveries did.
 */
export interface LiveryProfile {
  /** Catalogue item id this describes. */
  id: string;
  /**
   * Tiling pattern, projected triplanar in the mesh's own object space.
   *
   * `null` means this livery adds no pattern layer at all, which switches the
   * material plugin off rather than blending an empty one. That is what keeps
   * the stock tank's rendering byte-identical to what it was before any of this
   * existed — and it is the fallback a missing texture degrades to.
   */
  pattern: string | null;
  /**
   * Metres of hull covered by one repeat of `pattern`.
   *
   * A pitch in **metres**, not a UV repeat count, because the projection is in
   * object space and the meshes are normalised to a fixed length before being
   * scaled to 5.6 m. The same number therefore puts the same size of blotch on
   * every hull and on every turret, which is the whole point of projecting
   * rather than unwrapping. Turrets are scaled separately from hulls and the
   * plugin divides by whichever scale its material belongs to, so a 2.4 m
   * blotch is 2.4 m on both halves of the tank.
   *
   * Too small and the scheme is noise at match distance — see the README in
   * `~/Desktop/tank-2d-for-3d/liveries/` for the distance check.
   */
  patternScaleM: number;
  /**
   * How much of the authored albedo the pattern replaces, 0–1.
   *
   * Never 1. The mesh's own baked shading, panel lines, grime and ambient
   * occlusion live in that albedo, and a livery that covered them completely
   * would turn a detailed hull into a camouflaged brick. The plugin also
   * re-lights the pattern by the albedo's own luminance before mixing, so even
   * at this strength the vehicle's form still reads through the paint.
   */
  patternStrength: number;
  /** Multiplied into the authored albedo underneath the pattern. */
  baseTint: string;
  /**
   * Whole-vehicle finish, or `null` to keep whatever the GLB shipped with.
   *
   * These are the cheapest thing in the file and among the most visible: a
   * livery that sets a finish is a different *material* rather than a different
   * colour, and reads as lacquer or brass from any distance without a single
   * texture being fetched. They multiply the mesh's own metallic-roughness map
   * rather than replacing it, so the artist's wear pattern survives.
   *
   * **`metallic` is bounded by `MAX_LIVERY_METALLIC` and the bound is load-
   * bearing.** This documentation used to offer `metallic: 0.85, roughness: 0.18`
   * as the way to say "chrome or gold", and two liveries took it at its word and
   * shipped grey: there is no environment texture in this game, so everything a
   * metal surface stops paying into diffuse is paid into a reflection of nothing.
   * The way to say *gold* here is a saturated warm pattern under a **moderate**
   * metallic and a **low** roughness — colour from the albedo, expense from a
   * tight highlight. See the file header.
   */
  metallic: number | null;
  roughness: number | null;
}

/** Where livery art lives. One directory, the way `parts` and `ranks` have one. */
const ART = '/assets/liveries';

/**
 * No livery at all: no pattern and no finish override.
 *
 * The **failure** value, and only that. Returned for an unknown id, for a
 * withdrawn paint and for a caller with no loadout to hand. Every field is the
 * one that makes the renderer skip the work, so a livery that fails to resolve
 * degrades to whatever the mesh itself looks like rather than to a half-applied
 * paint, and it can never itself fail — there is no texture to fetch.
 *
 * **It used to be the issued coat as well, and is not any more.** That coat was
 * deliberately the identity while every hull and turret carried an authored
 * albedo: the mesh already looked like a painted tank, so the cheapest correct
 * thing was to leave it alone. The Smart Mesh intake removed that premise —
 * those meshes have no materials and no albedo detail at all, so "skip the work"
 * renders a white tank. The issued coat is a real livery now (`STOCK_COAT`), and
 * this stays the no-op underneath it.
 */
export const NO_LIVERY: LiveryProfile = {
  id: 'paint_none',
  pattern: null,
  patternScaleM: 2.4,
  patternStrength: 0,
  baseTint: '#ffffff',
  metallic: null,
  roughness: null,
};

/**
 * The id of the coat every tank wears before anybody buys anything.
 *
 * **Exported because three places used to hard-code it** — `garage.service.ts`
 * for what is consumable, and two frontend components for what the shelf may
 * re-apply — and a stock coat that four files disagree about is a paint that can
 * be sold to the player who already has it. One name, four readers.
 */
export const STOCK_PAINT_ID = 'paint_desert';

/**
 * The issued coat — what a tank looks like before anybody buys anything.
 *
 * **This is Desert Sand, and it used to be Standard Olive.** The olive was a
 * generated base coat written to be the quietest thing on the shelf: a standard
 * deviation of 3 against 37–71 for the bought schemes, on the argument that a
 * player who has bought nothing must not look like they chose a scheme. The
 * decision here is the opposite one — issue a real camouflage and have the tank
 * look finished from the first sortie — so that argument no longer applies and
 * the olive is gone rather than demoted. Its consequence is worth stating: the
 * stock tank now reads as *wearing* a scheme, which is the price of it not
 * reading as primer.
 *
 * It is an ordinary shelf livery in every other respect, and that is what makes
 * it cheap: the numbers below are the ones it carried when it cost 300 credits,
 * unchanged, so nothing about how it renders is a special case. What changed is
 * its price and who owns it.
 */
export const STOCK_COAT: LiveryProfile = {
  id: STOCK_PAINT_ID,
  pattern: `${ART}/pattern-desert.webp`,
  patternScaleM: 2.6,
  patternStrength: 0.78,
  baseTint: '#efe2c8',
  metallic: null,
  roughness: 0.86,
};

/**
 * Every livery in the catalogue, by item id.
 *
 * Anything missing resolves to `NO_LIVERY`, so a PAINT row added to
 * `catalogue.data.ts` without an entry here is a stock-looking tank rather than
 * a crash — the same fallback discipline `weaponFor` follows for turrets.
 */
export const LIVERY_PROFILES: Record<string, LiveryProfile> = {
  // The issued coat. A real livery since the Smart Mesh intake — see `STOCK_COAT`
  // for why the identity stopped being enough, and why this is Desert Sand.
  [STOCK_PAINT_ID]: STOCK_COAT,

  paint_winter: {
    id: 'paint_winter',
    pattern: `${ART}/pattern-winter.webp`,
    patternScaleM: 2.9,
    patternStrength: 0.8,
    baseTint: '#f2f5f7',
    metallic: null,
    roughness: 0.82,
  },

  paint_urban: {
    id: 'paint_urban',
    // Splinter camouflage is straight-edged, so it survives being cut across a
    // hatch or a fender in a way a soft-edged blotch does not — which is why
    // this one takes the largest pattern of the credit shelf without turning
    // into noise.
    pattern: `${ART}/pattern-urban.webp`,
    patternScaleM: 3.2,
    patternStrength: 0.82,
    baseTint: '#dfe3e8',
    metallic: 0.25,
    roughness: 0.7,
  },

  paint_woodland: {
    id: 'paint_woodland',
    pattern: `${ART}/pattern-woodland.webp`,
    patternScaleM: 2.5,
    patternStrength: 0.8,
    baseTint: '#e6e9de',
    metallic: null,
    roughness: 0.88,
  },

  paint_crimson: {
    id: 'paint_crimson',
    pattern: `${ART}/pattern-crimson.webp`,
    patternScaleM: 3.0,
    patternStrength: 0.85,
    baseTint: '#ffffff',
    // Lacquer: the one credit livery that changes the *finish* as well as the
    // colour, which is what makes it read as expensive from further away than
    // its pattern does.
    metallic: 0.35,
    roughness: 0.32,
  },

  paint_gilded: {
    id: 'paint_gilded',
    pattern: `${ART}/pattern-gilded.webp`,
    patternScaleM: 1.9,
    patternStrength: 0.92,
    baseTint: '#ffffff',
    // **The repair, and it is the whole reason `MAX_LIVERY_METALLIC` exists.**
    // This was `0.95 / 0.16`, which is what a physically-correct gold leaf is and
    // what this renderer cannot draw: with no environment texture in the scene,
    // 95% of the gold albedo was paid into a reflection of nothing and the tight
    // roughness clipped the sun's return to white. The card promised *gold leaf
    // on a war machine* and the arena delivered a bone-cream tank. The tell at
    // the time was that its belt — then part of a livery, and a
    // `StandardMaterial` that never went through any of this — was the only gold
    // on the vehicle. The belts are stock for every paint now, so a livery that
    // fails this way has nothing left to give the game away with.
    //
    // The gold now comes from where it can actually come from: the pattern's own
    // saturated albedo, 65% of which survives at this metallic. What makes it
    // read as *metal* rather than as yellow paint is the roughness — the tightest
    // on the shelf, so the sun draws a hard bright edge along every panel that
    // moves as the tank turns. Expense from the highlight, colour from the paint.
    metallic: 0.35,
    roughness: 0.26,
  },

  // **Withdrawn from sale — see `retired` on its catalogue row.** The halftone
  // is pitched too fine to survive 80 m and the pattern is dark and saturated,
  // which is the second half of the rule in the file header. The profile stays
  // because the row stays: nobody currently owns it, and if anybody did they
  // would keep a tank that still paints correctly rather than a tank that stopped
  // resolving. Deleting this entry is what would confiscate it.
  paint_ambush: {
    id: 'paint_ambush',
    pattern: `${ART}/pattern-ambush.webp`,
    patternScaleM: 2.8,
    patternStrength: 0.82,
    baseTint: '#e8e6da',
    metallic: null,
    roughness: 0.85,
  },

  paint_hazard: {
    id: 'paint_hazard',
    pattern: `${ART}/pattern-hazard.webp`,
    patternScaleM: 3.4,
    patternStrength: 0.86,
    baseTint: '#ffffff',
    metallic: 0.4,
    roughness: 0.55,
  },

  paint_scorched: {
    id: 'paint_scorched',
    pattern: `${ART}/pattern-scorched.webp`,
    patternScaleM: 2.2,
    // 0.88 → 0.92. The finest texture in the pack is also the darkest, so it
    // loses the most to the luminance re-light described in the file header;
    // letting more of it through is the only lever this file has against that,
    // short of changing the shader for every livery in the game.
    patternStrength: 0.92,
    baseTint: '#ffffff',
    // Was `0.8 / 0.3` — the same failure as the Gilded one notch down, and it
    // cost the iridescence the whole scheme is named for. Temper colours are a
    // thin oxide film on steel: the hue is in the film, and a metal that has
    // nothing to reflect cannot carry a hue at all.
    metallic: 0.3,
    roughness: 0.34,
  },

  // ── The twelve added when the shelf went to twenty-one ────────────────────
  //
  // Every one of these is authored **value first and colour second**, which is
  // the rule the file header argues and the rule the retired `paint_ambush`
  // broke. The shader multiplies the pattern by
  // `SHADE_FLOOR + SHADE_GAIN * luma(albedo)` before mixing, so what survives to
  // eighty metres is a *light-against-dark shape*; a hue at low value and any
  // detail finer than the shape scale are both crushed. Each entry names its
  // shape so the next author can check the picture against the intent rather
  // than against taste.

  // Shape: five hard near-white wedges against charcoal. The strongest value
  // structure on the shelf — dazzle was invented to be read at distance in bad
  // light, which is the same problem this renderer sets.
  paint_dazzle: {
    id: 'paint_dazzle',
    pattern: `${ART}/pattern-dazzle.webp`,
    patternScaleM: 3.4,
    patternStrength: 0.88,
    baseTint: '#ffffff',
    metallic: null,
    roughness: 0.62,
  },

  // Shape: long wavy ribbons of ochre and umber across a pale sand ground.
  paint_steppe: {
    id: 'paint_steppe',
    pattern: `${ART}/pattern-steppe.webp`,
    patternScaleM: 3,
    patternStrength: 0.8,
    baseTint: '#efe6cf',
    metallic: null,
    roughness: 0.86,
  },

  // Shape: stacked chevrons, dark and pale on mid grey — straight-edged, so it
  // survives being cut across a hatch the way the splinter pattern does.
  paint_chevron: {
    id: 'paint_chevron',
    pattern: `${ART}/pattern-chevron.webp`,
    patternScaleM: 2.6,
    patternStrength: 0.82,
    baseTint: '#e3e7ea',
    metallic: 0.15,
    roughness: 0.72,
  },

  // Shape: four wide dark ribs across a pale khaki ground.
  paint_tiger: {
    id: 'paint_tiger',
    pattern: `${ART}/pattern-tiger.webp`,
    patternScaleM: 2.7,
    patternStrength: 0.8,
    baseTint: '#efe6cf',
    metallic: null,
    roughness: 0.86,
  },

  // Shape: pale crust plates divided by dark fissures — light held together by
  // a dark web, the inverse of the woodland blotch and legible for it.
  paint_saltflat: {
    id: 'paint_saltflat',
    pattern: `${ART}/pattern-saltflat.webp`,
    patternScaleM: 2.9,
    patternStrength: 0.82,
    baseTint: '#f4f2ea',
    metallic: null,
    roughness: 0.8,
  },

  // Shape: soft pale masses over deep slate. Feathered edges, but the *value*
  // break is hard, which is the half that has to survive.
  paint_thunderhead: {
    id: 'paint_thunderhead',
    pattern: `${ART}/pattern-thunderhead.webp`,
    patternScaleM: 3.1,
    patternStrength: 0.8,
    baseTint: '#e9edf1',
    metallic: null,
    roughness: 0.78,
  },

  // Shape: pale mint blooms on dark bronze. The colour is the point of the
  // scheme and the *blooms* are what carries it — a patina stated as a hue over
  // an even ground would have been the scorched-iron mistake again.
  paint_verdigris: {
    id: 'paint_verdigris',
    pattern: `${ART}/pattern-verdigris.webp`,
    patternScaleM: 2.6,
    patternStrength: 0.84,
    baseTint: '#ffffff',
    metallic: 0.3,
    roughness: 0.58,
  },

  // Shape: bright amber cracks on near-black — the one scheme on the shelf that
  // is light *on* dark rather than dark on light, and the reason its strength is
  // the highest here: the black ground is most of the tile, so the cracks need
  // to arrive intact.
  paint_ember: {
    id: 'paint_ember',
    pattern: `${ART}/pattern-ember.webp`,
    patternScaleM: 2.4,
    patternStrength: 0.9,
    baseTint: '#ffffff',
    // Warm albedo under a tight roughness, which is the file header's recipe for
    // anything that should read as hot or as metal without an environment to
    // reflect. Colour from the paint, expense from the highlight.
    metallic: 0.32,
    roughness: 0.4,
  },

  // Shape: four flat values in large hexagonal plates. Geometric like the urban
  // splinter, but at plate scale rather than shard scale.
  paint_hexfield: {
    id: 'paint_hexfield',
    pattern: `${ART}/pattern-hexfield.webp`,
    patternScaleM: 3.3,
    patternStrength: 0.84,
    baseTint: '#e8ecf0',
    metallic: 0.25,
    roughness: 0.66,
  },

  // Shape: broad ivory plates on dark umber seams.
  paint_bonerelic: {
    id: 'paint_bonerelic',
    pattern: `${ART}/pattern-bonerelic.webp`,
    patternScaleM: 2.8,
    patternStrength: 0.82,
    baseTint: '#f5f0e4',
    metallic: null,
    roughness: 0.84,
  },

  // Shape: pale ash banks drifted over basalt.
  paint_ashfall: {
    id: 'paint_ashfall',
    pattern: `${ART}/pattern-ashfall.webp`,
    patternScaleM: 3.0,
    patternStrength: 0.84,
    baseTint: '#eceff2',
    metallic: null,
    roughness: 0.88,
  },

  // Shape: four hard diagonal bands, pale grey against petrol black.
  paint_stormtide: {
    id: 'paint_stormtide',
    pattern: `${ART}/pattern-stormtide.webp`,
    patternScaleM: 3.2,
    patternStrength: 0.86,
    baseTint: '#ffffff',
    metallic: 0.28,
    roughness: 0.6,
  },

  // Shape: thick dark veins branching across pale stone. Veins rather than
  // lines, deliberately — a marble drawn at its real fineness is exactly the
  // high-frequency detail the re-light destroys.
  paint_quarry: {
    id: 'paint_quarry',
    pattern: `${ART}/pattern-quarry.webp`,
    patternScaleM: 3.0,
    patternStrength: 0.84,
    baseTint: '#f6f4ef',
    metallic: 0.22,
    roughness: 0.52,
  },

  // ── The three added on 2026-10-15 (Higgsfield art, made seamless and snapped
  // back to flat paint colours) ─────────────────────────────────────────────

  // Shape: mid-value leaf masses in four warm hues and pine over a pale sand
  // ground. The ground is what keeps it legible; the hues are the product.
  paint_autumnflora: {
    id: 'paint_autumnflora',
    pattern: `${ART}/pattern-autumnflora.webp`,
    patternScaleM: 2.6,
    patternStrength: 0.9,
    baseTint: '#f5eddc',
    metallic: null,
    roughness: 0.84,
  },

  // Shape: a lattice of bone-white rims and near-black diamonds over olive
  // scales. The lattice is the value structure that survives range; the scales
  // inside it are texture and go first, which is fine. Mirrored to tile, which
  // a snake's own symmetry hides.
  paint_viperhide: {
    id: 'paint_viperhide',
    pattern: `${ART}/pattern-viperhide.webp`,
    patternScaleM: 3.0,
    patternStrength: 0.86,
    baseTint: '#f0ede0',
    metallic: null,
    roughness: 0.86,
  },

  // Shape: straight-edged dark pine, green and brown shards with sage between —
  // the hardest value structure on the shelf after dazzle, so it takes a large
  // pitch and survives being cut across hatches like the urban splinter. Matte
  // and lightly tinted like woodland: any sheen washes a green camo towards pale.
  paint_pineshard: {
    id: 'paint_pineshard',
    pattern: `${ART}/pattern-pineshard.webp`,
    patternScaleM: 3.2,
    patternStrength: 0.97,
    baseTint: '#e6e9de',
    metallic: null,
    roughness: 0.86,
  },

  // Shape: bright zinc primer flaking to dark rust in ragged patches.
  paint_oxide: {
    id: 'paint_oxide',
    pattern: `${ART}/pattern-oxide.webp`,
    patternScaleM: 2.5,
    patternStrength: 0.86,
    baseTint: '#ffffff',
    metallic: null,
    roughness: 0.82,
  },
};

/**
 * The livery for a paint id, always.
 *
 * Total, like `weaponFor` and `modelForHull`: an unknown id, a null, a row
 * added to the catalogue without art — all resolve to the stock look. A
 * cosmetic lookup must never be able to stop a tank being drawn.
 */
export function liveryFor(paintId: string | null | undefined): LiveryProfile {
  // **Absent and unresolvable are different answers, and they were the same
  // answer until the Smart Mesh intake.**
  //
  // *Absent* — a player who has chosen no paint, and every bot, which has no
  // account to choose one from — means the issued coat. That used to be the
  // identity and rendered the mesh's own authored albedo, which was a painted
  // tank; on an untextured mesh it renders white, so an arena would be eight
  // white tanks. The default state has to state something.
  //
  // *Unresolvable* — an id naming a paint that does not exist, or one that has
  // been withdrawn — **also means the issued coat**, and it used to mean
  // `NO_LIVERY`. That was wrong, and it was wrong for exactly the reason the
  // paragraph above gives: `NO_LIVERY` leaves the plugin off, and with the plugin
  // off an untextured Smart Mesh renders white. The old reasoning was that a
  // withdrawn paint must not silently become the issued coat, or removing a
  // livery from the shelf would repaint everyone who owned it — and that was the
  // right trade while `NO_LIVERY` still drew the mesh's own authored albedo, which
  // was a painted tank. It is not the right trade against an *invisible* one.
  //
  // It shipped, and it was reported as **"the garage doesn't show the tank, and in
  // battle there are no hulls or turrets, only the caterpillars"** — the belts are
  // a `StandardMaterial` this codebase builds itself, so they were the only part
  // still drawing. `Standard Olive` had been withdrawn from the shelf and 59 of 69
  // accounts still had `paint_olive` equipped, so nearly every player in the
  // database was driving a tank made of two tracks.
  //
  // Both halves needed fixing and this is the half that cannot fail again: a
  // repaint is a cosmetic surprise a player can undo in the garage, and an
  // invisible tank is not a game. `prisma/repaint-withdrawn.ts` is the other half
  // — it moves the accounts, so nobody has to wait for a lazy repair to notice.
  //
  // `NO_LIVERY` is kept and still means what it says: a profile that deliberately
  // states no pattern. It is simply no longer what an *unknown id* lands on.
  if (paintId === null || paintId === undefined || paintId === '') return STOCK_COAT;
  return LIVERY_PROFILES[paintId] ?? STOCK_COAT;
}

/**
 * Whether two liveries are the same paint.
 *
 * By id rather than by reference or by field, because the profiles are shared
 * module objects and the id is the identity — the same argument `sameGear` and
 * `sameFit` make in `tank.ts`, arrived at from the other side: those compare
 * fields *because* two different ids can describe the same mesh, and this
 * compares ids because two liveries never legitimately share one.
 */
export function sameLivery(a: LiveryProfile, b: LiveryProfile): boolean {
  return a.id === b.id;
}
